import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { formatTimestampShort } from '@chainflip/utils/date';
import { abbreviate } from '@chainflip/utils/string';
import { MAX_TICK, rateToTick } from '@chainflip/utils/tickMath';
import classNames from 'classnames';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { RangerOrderStatus } from '@/shared/components/orders/OrderStatus';
import { PoolFeeBadge } from '@/shared/components/pools/PoolFeeBadge';
import DistanceIcon from '@/shared/icons/flip-ui-kit/small/Distance';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/small/MinusCircle';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/small/PlusCircle';
import { chainflipAssetMap } from '@/shared/utils/env';
import { getFormattedTickRate } from '@/shared/utils/tickMath';
import TokenAmount from '@/shared/utils/TokenAmount';

export const BuyPill = () => <Pill color="green" text="Buy" Icon={PlusCircleIcon} />;

export const SellPill = () => <Pill color="red" text="Sell" Icon={MinusCircleIcon} />;

export const OrderTypePill = ({ type }: { type: 'LIMIT' | 'RANGE' }) => (
  <Pill color="neutral" text={type === 'LIMIT' ? 'Limit' : 'Range'} />
);

export const OrderPrice = ({
  tick,
  baseAsset,
}: {
  tick: number | string;
  baseAsset: BaseChainflipAsset;
}) => {
  const allDecimalsString = getFormattedTickRate(tick, baseAsset);
  const defaultDecimalsString = getFormattedTickRate(tick, baseAsset, 4);
  const exceedsDecimals = allDecimalsString.length > defaultDecimalsString.length;

  return (
    <div className="inline-block">
      <Tooltip
        content={exceedsDecimals ? allDecimalsString : defaultDecimalsString}
        pointer={false}
      >
        {defaultDecimalsString}
      </Tooltip>
    </div>
  );
};

type RangeOrderRowProps = {
  valueUsd?: string | null;
  baseAsset: BaseChainflipAsset;
  lpAccount: string | null;
  creationTimestamp?: string;
  displayCreationTimestamp?: boolean;
  onClick?: (asset: BaseChainflipAsset) => void;
  isMobileView?: boolean;
  baseAmount: bigint | undefined;
  quoteAmount: bigint | undefined;
  liquidityFeeHundredthPips?: number;
  baseFeesEarned: bigint;
  quoteFeesEarned: bigint;
  lowerTick: number;
  upperTick: number;
  isRpcOrderOpen: boolean;
  currentRangeOrderPrice: number;
  additionalCells?: React.ReactNode;
};

type RangeOrderRowMobileProps = Omit<
  RangeOrderRowProps,
  | 'onClick'
  | 'valueUsd'
  | 'lpAccount'
  | 'baseAmount'
  | 'quoteAmount'
  | 'baseFeesEarned'
  | 'quoteFeesEarned'
  | 'additionalCells'
  | 'currentRangeOrderPrice'
> & {
  isInRange: boolean;
  baseTokenAmount: TokenAmount | null;
  earnedFeesBaseAsset: TokenAmount;
  quoteTokenAmount: TokenAmount | null;
  earnedFeesQuoteAsset: TokenAmount;
};

type RangeOrderRowDesktopProps = RangeOrderRowMobileProps &
  Pick<RangeOrderRowProps, 'onClick' | 'valueUsd' | 'lpAccount' | 'additionalCells'>;

const RangeOrderRowMobile = ({
  isRpcOrderOpen,
  isInRange,
  baseAsset,
  baseTokenAmount,
  earnedFeesBaseAsset,
  quoteTokenAmount,
  earnedFeesQuoteAsset,
  lowerTick,
  upperTick,
  displayCreationTimestamp = true,
  creationTimestamp,
  liquidityFeeHundredthPips,
}: RangeOrderRowMobileProps) => {
  const usdcSymbol = chainflipAssetMap.Usdc.symbol;

  return (
    <tr className="flex flex-col justify-center gap-y-2 p-3 text-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <OrderTypePill type="RANGE" />
          {liquidityFeeHundredthPips && (
            <PoolFeeBadge color="purple" liquidityFeeHundredthPips={liquidityFeeHundredthPips} />
          )}
        </div>
        <RangerOrderStatus isRpcOrderOpen={isRpcOrderOpen} isInRange={isInRange} />
      </div>
      <div className="grid w-full grid-cols-[auto,auto] content-start justify-between gap-1">
        <span className="flex gap-x-2 text-white">
          <TokenWithChainByAsset asset={baseAsset} /> {baseTokenAmount?.toFormatted()}{' '}
          {chainflipAssetMap[baseAsset].symbol}
        </span>
        <div
          className={classNames(earnedFeesBaseAsset.toBigInt() > 0 && 'ml-auto text-cf-green-3')}
        >
          {earnedFeesBaseAsset.toBigInt() > 0 && '+'}
          {earnedFeesBaseAsset?.toFormatted()} {chainflipAssetMap[baseAsset].symbol}
        </div>

        <span className="flex gap-x-2 text-white">
          <TokenWithChainByAsset asset="Usdc" /> {quoteTokenAmount?.toFormatted()}{' '}
          {chainflipAssetMap.Usdc.symbol}
        </span>
        <div
          className={classNames(earnedFeesQuoteAsset.toBigInt() > 0 && 'ml-auto text-cf-green-3')}
        >
          {earnedFeesQuoteAsset.toBigInt() > 0 && '+'}
          {earnedFeesQuoteAsset?.toFormatted()} {chainflipAssetMap.Usdc.symbol}
        </div>
      </div>
      <div className="flex items-center gap-x-2">
        <OrderPrice baseAsset={baseAsset} tick={lowerTick} /> {usdcSymbol}
        <DistanceIcon className="text-cf-light-1" />
        <OrderPrice baseAsset={baseAsset} tick={upperTick} /> {upperTick < MAX_TICK && usdcSymbol}
      </div>
      {displayCreationTimestamp && (
        <span>
          {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
        </span>
      )}
    </tr>
  );
};

const RangeOrderRowDesktop = ({
  onClick,
  baseAsset,
  baseTokenAmount,
  quoteTokenAmount,
  lowerTick,
  upperTick,
  earnedFeesBaseAsset,
  earnedFeesQuoteAsset,
  lpAccount,
  isRpcOrderOpen,
  isInRange,
  displayCreationTimestamp = true,
  creationTimestamp,
  additionalCells,
  liquidityFeeHundredthPips,
}: RangeOrderRowDesktopProps) => (
  <ClickableTableRow
    disableHoverStyle={!onClick}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(baseAsset);
    }}
  >
    <td className="whitespace-nowrap">
      <div className="flex items-center gap-2">
        <OrderTypePill type="RANGE" />
        {liquidityFeeHundredthPips && (
          <PoolFeeBadge color="purple" liquidityFeeHundredthPips={liquidityFeeHundredthPips} />
        )}
      </div>
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex flex-col gap-y-2 text-cf-light-3">
        <span className="flex items-center gap-x-2">
          <TokenWithChainByAsset asset={baseAsset} size="medium" />
          <span>{baseTokenAmount?.toFormatted()}</span>
          <span>{chainflipAssetMap[baseAsset].symbol}</span>
          <div className={classNames(earnedFeesBaseAsset.toBigInt() > 0 && 'text-cf-green-3')}>
            (+{earnedFeesBaseAsset.toFormatted()})
          </div>
          <span className="text-cf-light-2">on {chainflipAssetMap[baseAsset].chain.name}</span>
        </span>
        <span className="flex items-center gap-x-2">
          <TokenWithChainByAsset asset="Usdc" size="medium" />
          <span>{quoteTokenAmount?.toFormatted()}</span>
          <span>{chainflipAssetMap.Usdc.symbol}</span>
          <div className={classNames(earnedFeesQuoteAsset.toBigInt() > 0 && 'text-cf-green-3')}>
            (+{earnedFeesQuoteAsset.toFormatted()})
          </div>
          <span className="text-cf-light-2">on {chainflipAssetMap.Usdc.chain.name}</span>
        </span>
      </div>
    </td>
    <td className="flex whitespace-nowrap text-12">
      <div className="flex flex-col items-center">
        <OrderPrice baseAsset={baseAsset} tick={lowerTick} />
        <DistanceIcon className="text-cf-light-1" />
        <OrderPrice baseAsset={baseAsset} tick={upperTick} />
      </div>
    </td>
    {lpAccount && (
      <td className="whitespace-nowrap text-12">
        <Link href={`/lps/${lpAccount}`} noPropagate underline>
          <span>{abbreviate(lpAccount)}</span>
        </Link>
      </td>
    )}
    <td className="whitespace-nowrap text-12">
      <RangerOrderStatus isRpcOrderOpen={isRpcOrderOpen} isInRange={isInRange} />
    </td>
    {displayCreationTimestamp && (
      <td className="whitespace-nowrap text-12">
        {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
      </td>
    )}
    {additionalCells}
  </ClickableTableRow>
);

export const RangeOrderRow = (props: RangeOrderRowProps) => {
  const baseTokenAmount = TokenAmount.fromAsset(props.baseAmount, props.baseAsset);
  const quoteTokenAmount = TokenAmount.fromAsset(props.quoteAmount, 'Usdc');
  const earnedFeesBaseAsset = TokenAmount.fromAsset(props.baseFeesEarned, props.baseAsset);
  const earnedFeesQuoteAsset = TokenAmount.fromAsset(props.quoteFeesEarned, 'Usdc');

  const currentTick = rateToTick(props.currentRangeOrderPrice, props.baseAsset);
  const isInRange = currentTick >= props.lowerTick && currentTick <= props.upperTick;

  return props.isMobileView ? (
    <RangeOrderRowMobile
      {...props}
      isInRange={isInRange}
      baseTokenAmount={baseTokenAmount}
      earnedFeesBaseAsset={earnedFeesBaseAsset}
      quoteTokenAmount={quoteTokenAmount}
      earnedFeesQuoteAsset={earnedFeesQuoteAsset}
    />
  ) : (
    <RangeOrderRowDesktop
      {...props}
      isInRange={isInRange}
      baseTokenAmount={baseTokenAmount}
      earnedFeesBaseAsset={earnedFeesBaseAsset}
      quoteTokenAmount={quoteTokenAmount}
      earnedFeesQuoteAsset={earnedFeesQuoteAsset}
    />
  );
};
